import {Component, input, output, inject} from '@angular/core';
import {EmployeeRank} from '../../../classes/models/employee/EmployeeRank';
import {TableHeader} from '../../../shared/classes/TableHeader';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EmployeeRankFormComponent} from './employee-rank-form/employee-rank-form.component';
import {EmployeeRankDeleteComponent} from './employee-rank-delete/employee-rank-delete.component';
import {DataService} from '../../../services/store/data.service';
import {EmployeePositionId} from '../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';

/**
 * This component handles displaying and editing employee ranks.
 */
@Component({
  selector: 'app-employee-rank',
  templateUrl: './employee-rank.component.html',
  styleUrls: ['./employee-rank.component.less'],
  imports: [SHARED_IMPORTS]
})
export class EmployeeRankComponent {

  /** Identifiers of an employee and position. */
  readonly employeePositionId = input<EmployeePositionId>(undefined);
  /** A list of employee ranks to be displayed. */
  readonly employeeRanks = input<EmployeeRank[]>([]);
  /** An event to refresh employee page data. */
  readonly getEmployeePageDataEvent = output();

  /** Headers of employee ranks table. */
  headers: TableHeader[] = [
    new TableHeader('Rank', 'rank', false),
    new TableHeader('Start date', 'startDate', false),
    new TableHeader('', '', false)
  ];

  private readonly modalService = inject(NgbModal);
  private readonly dataService = inject(DataService);

  /**
   * Checks if data is allowed to edit at the given date.
   * @param date the date on which data might be edited.
   */
  canEditAtDate(date: string): boolean {
    return this.dataService.canEditAtDate(date);
  }

  /**
   * Opens a modal to create a new employee rank.
   */
  openCreate(): void {
    const modalRef = EmployeeRankFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      weekExists: false
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal to update an existing employee rank.
   */
  openUpdate(employeeRank: EmployeeRank): void {
    const modalRef = EmployeeRankFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      previousEmployeeRank: employeeRank,
      weekExists: true
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal to delete an existing employee rank.
   * @param employeeRank employee rank to delete.
   */
  openDelete(employeeRank: EmployeeRank): void {
    const modalRef = EmployeeRankDeleteComponent.open(this.modalService, {
      employeeRank,
      employeePositionId: this.employeePositionId()
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }
}